import { flatten, isEmpty, uniq, uniqBy } from 'lodash'

import { Address } from 'components/UI'
import { store } from 'store/reducers'
import { hasAccessToThis, getReportsToProjects, getReportsToTeams, getReportsToPositions } from 'services/Utilities'
import { FilterType, OptionsType } from 'services/types'

import { currentLang, t } from 'translations'
import { getAttributesQueryCache } from 'Api'

export const getFilterInfo = (options: string[] | string, loggedInUserId?: string, contentName?: string): FilterType[] => {
  if (!Array.isArray(options)) options = [options]
  const currentLanguage = currentLang()
  const filters: FilterType[] = []
  const otherFilters: OptionsType[] = []
  const {
    users: { userInfo },
    resources: {
      permissions,
      departments,
      hiringData,
      scheduleTypes,
      scheduleTemplates,
      tags,
      teams,
      projects,
      positions,
      timeOffTypes,
      users,
      projectOwners,
      usersById,
      requestStatus,
      attendanceTypes,
      attendanceReasons,
      wagePremiums
    },
    settings: { defaultRegularEarningCode = 'G1' }
  } = store.getState() as MStore.RootState

  const data = getAttributesQueryCache()
  const attributes = data?.attributes

  const { _id: userId } = userInfo || {}
  const limitToReportsTo = hasAccessToThis('limitToReportsTo')

  // Get the reportsTo resources for the userId
  const projectsOptions = limitToReportsTo ? getReportsToProjects(userId) : projects
  const teamsOptions = limitToReportsTo ? getReportsToTeams(userId) : teams
  const positionsOptions = limitToReportsTo ? getReportsToPositions(userId) : positions

  const earningCodes = [defaultRegularEarningCode, ...wagePremiums.map(({ earningCode }) => earningCode)]

  options.map(async option => {
    switch (option) {
      // - - USERS - - //
      case 'users': {
        // Make sure that users is defined
        if (isEmpty(users)) break
        const notArchived: OptionsType[] = []
        const archived: OptionsType[] = []
        users.forEach(user => {
          ;(user.archived ? archived : notArchived).push({
            label: `${user.firstName} ${user.lastName} (${user.employeeNumber})`,
            _id: user._id,
            alt: { email: user.emails?.map(({ address }) => address).join(' '), employeeNumber: user.employeeNumber },
            subLabel: user.archived ? <div className="text-danger font-weight-bold">{t(`manager.users.ARCHIVED`)}</div> : undefined,
            archived: user.archived
          })
        })
        filters.push({
          label: t('searchBar.USER'),
          type: 'users',
          icon: 'fa-user',
          key: 1,
          options: notArchived.concat(archived)
        })
        break
      }
      case 'createdBy': {
        // Make sure that users is defined
        if (isEmpty(users)) break

        const options: OptionsType[] = []
        users.forEach(({ _id, fullName, employeeNumber, archived }) => {
          if (archived === true) return
          options.push({
            label: t('searchBar.CREATED_BY_LABEL', { fullName, employeeNumber }),
            _id: _id
          })
        })
        filters.push({
          label: t('searchBar.CREATED_BY'),
          type: 'createdBy',
          icon: 'fa-users',
          options: options,
          key: 59
        })
        break
      }

      // -- REPORTS TO -- //
      case 'reportsTo': {
        // Make sure that users is defined
        if (isEmpty(users)) break

        const options: OptionsType[] = []
        // Get all users that have a team reporting to them
        const allReportsTo = uniq([...flatten(teams.map(({ reportsTo }) => reportsTo)), ...projectOwners.map(owner => owner._id)])

        allReportsTo.forEach(userId => {
          const user = usersById[userId]
          if (!user || user.archived === true) return
          options.push({
            label: loggedInUserId === userId ? t('searchBar.REPORTS_TO_ME') : t('searchBar.REPORTS_TO_NAME', { fullName: user.fullName }),
            _id: `reportsTo-${user._id}`
          })
        })

        filters.push({
          label: t('searchBar.REPORTS_TO'),
          type: 'reportsTo',
          icon: 'fa-users',
          key: 13,
          options
        })
        break
      }

      // - - ATTRIBUTES - - //
      case 'attributes': {
        // Make sure that attributes is defined
        if (isEmpty(attributes)) break

        filters.push({
          label: t('searchBar.ATTRIBUTES'),
          type: 'attributes',
          icon: 'fa-graduation-cap',
          key: 60,
          options:
            attributes?.map(attribute => ({
              label: attribute.name,
              icon:
                attribute.type === 'restriction'
                  ? 'fa-exclamation-triangle'
                  : attribute.type === 'skill'
                  ? 'fa-graduation-cap'
                  : attribute.type === 'alert'
                  ? 'fa-exclamation-triangle'
                  : 'fa-clipboard-check',
              _id: attribute._id
            })) ?? []
        })
        break
      }

      // - - EARNING CODES - - //
      case 'earningCodes': {
        // Make sure that attributes is defined
        if (isEmpty(wagePremiums)) break

        filters.push({
          label: t('searchBar.EARNING_CODE'),
          type: 'earningCodes',
          icon: 'fa-barcode',
          key: 61,
          options: earningCodes.map(earningCode => ({
            label: earningCode,
            _id: earningCode
          }))
        })
        break
      }

      // - - NO ATTRIBUTES - - //
      case 'notAttributes': {
        // Make sure that noAttributes is defined
        if (isEmpty(attributes)) break

        filters.push({
          label: t('searchBar.MISSING_ATTRIBUTES'),
          type: 'notAttributes',
          icon: 'fa-graduation-cap',
          key: 61,
          options:
            attributes?.map(attribute => ({
              label: t('searchBar.HAS_NOT_ATTRIBUTE', { name: attribute.name }),
              _id: `not-${attribute._id}`
            })) ?? []
        })
        break
      }

      case 'attributeTypes': {
        filters.push({
          label: t('searchBar.ATTRIBUTE_TYPES'),
          type: 'type',
          icon: 'fa-graduation-cap',
          key: 62,
          options: [
            {
              label: t('searchBar.RESTRICTION'),
              icon: 'fa-exclamation-triangle',
              _id: 'restriction'
            },
            {
              label: t('searchBar.SKILL'),
              icon: 'fa-graduation-cap',
              _id: 'skill'
            },
            {
              label: t('searchBar.REQUIREMENT'),
              icon: 'fa-clipboard-check',
              _id: 'requirement'
            }
          ]
        })
        break
      }
      // - - POSITIONS - - //
      case 'eligibleForPosition':
      case 'positions': {
        // Make sure that positions is defined
        if (isEmpty(positionsOptions)) break
        if (option === 'positions')
          filters.push({
            label: t('searchBar.POSITION'),
            type: 'positions',
            icon: 'fa-map-pin',
            key: 2,
            options: positionsOptions.map(position => ({
              label: position.name,
              _id: position._id
            }))
          })
        if (option === 'eligibleForPosition')
          filters.push({
            label: t('searchBar.USER_ELIGIBLE_FOR_POSITION'),
            type: 'eligibleForPosition',
            icon: 'fa-map-pin',
            key: 3,
            options: positionsOptions.map(position => ({
              label: t('searchBar.ELIGIBLE_FOR', { position: position.name }),
              _id: `eligible-${position._id}`
            }))
          })
        break
      }
      // - - NO POSITIONS - - //
      case 'notPositions': {
        // Make sure that positions is defined
        if (isEmpty(positionsOptions)) break
        filters.push({
          label: t('searchBar.MISSING_POSITIONS'),
          type: 'notPositions',
          icon: 'fa-map-pin',
          key: 90,
          options: positionsOptions.map(position => ({
            label: t('searchBar.HAS_NOT_POSITION', { name: position.name }),
            _id: '!' + position._id
          }))
        })
        break
      }
      // - - DEPARTMENTS - - //
      case 'departments': {
        // Make sure that departments is defined
        if (isEmpty(departments)) break
        filters.push({
          label: t('searchBar.DEPARTMENT'),
          type: 'departments',
          icon: 'fa-building',
          key: 4,
          options: departments.map(department => ({
            label: department[`name${currentLanguage.toUpperCase()}`],
            _id: department._id
          }))
        })
        break
      }
      // - - TEAMS  - - //
      case 'teams': {
        // Make sure that teams is defined
        if (isEmpty(teamsOptions)) break
        filters.push({
          label: t('searchBar.TEAM'),
          type: 'teams',
          displayOptions: true,
          icon: 'fa-users-class',
          key: 5,
          options: teamsOptions.map(team => ({
            label: team.name,
            _id: team._id
          }))
        })
        break
      }

      // - - PERMISSIONS - - //
      case 'permissions': {
        // Make sure that permissions is defined
        if (isEmpty(permissions)) break
        filters.push({
          label: t('searchBar.PERMISSION'),
          type: 'permissions',
          icon: 'fa-sliders-h',
          key: 68,
          options: permissions.map(permission => ({
            label: permission[`name${currentLanguage.toUpperCase()}`],
            _id: permission._id
          }))
        })
        break
      }
      // - - SHOW REGIONAL TAGS - - //
      case 'regionalTags': {
        const regional: OptionsType[] = []
        tags.forEach(({ _id, type, name, icon }) => {
          if (type === 'regional') regional.push({ label: name, _id, icon })
        })
        if (!regional.length) break

        filters.push({
          icon: 'fa-map-marked-alt',
          type: 'regionalTags',
          options: regional,
          label: t('searchBar.REGION'),
          key: 6
        })
        break
      }

      case 'tags': {
        filters.push({
          label: t('searchBar.TAGS'),
          type: 'tags',
          icon: 'fa-address-card',
          key: 71,
          options: tags.map(({ _id, name: label, icon, address, color }) => ({
            label,
            icon,
            color,
            _id,
            subLabel: address ? <Address address={address} short /> : undefined
          }))
        })
        break
      }
      case 'tagTypes': {
        filters.push({
          label: t('searchBar.TAGS_TYPES'),
          type: 'tagTypes',
          icon: 'fa-address-card',
          key: 72,
          options: [
            {
              label: t('searchBar.CLIENTS'),
              icon: 'fas fa-suitcase',
              _id: 'clients'
            },
            {
              label: t('searchBar.LOCATION'),
              icon: 'far fa-building',
              _id: 'locations'
            },
            {
              label: t('searchBar.RESOURCES'),
              icon: 'fas fa-barcode-read',
              _id: 'resources'
            },
            {
              label: t('searchBar.REGION'),
              icon: 'fa-map-marked-alt',
              _id: 'regional'
            },
            {
              label: t('searchBar.CUSTOMTAG'),
              icon: 'fa fa-tag',
              _id: 'custom'
            }
          ]
        })
        break
      }
      // - - SHOW CLIENTS TAGS - - //
      case 'clients': {
        const clients: OptionsType[] = []
        tags.forEach(({ _id, type, name, icon }) => {
          if (type === 'clients') clients.push({ label: name, _id, icon })
        })

        filters.push({
          icon: 'fa fa-suitcase',
          options: clients,
          label: t('searchBar.CLIENTS'),
          type: 'clients',
          key: 7
        })
        break
      }

      // - - SHOW REQUEST STATUS - - //
      case 'requestStatusIds': {
        // Don't add tags if there are no requestFields status
        if (isEmpty(requestStatus)) break

        const filterAttendances = contentName === 'attendances'
        const areAttendanceStatuses = id => id !== 'APPROVED'
        const areVacationStatuses = id => !['ACKNOWLEDGED', 'JUSTIFIED', 'UNJUSTIFIED'].includes(id)
        filters.push({
          label: t('searchBar.REQUEST_STATUS'),
          type: 'requestStatusIds',
          icon: 'fa-flag',
          key: 73,
          options: requestStatus
            .filter(id => (filterAttendances ? areAttendanceStatuses(id) : areVacationStatuses(id)))
            .map(status => ({
              _id: status,
              label: t(status)
            }))
        })
        break
      }
      // - - SHOW LEAVE TYPE - - //
      case 'leaveType': {
        // Don't add tags if there are no requestFields status
        if (isEmpty(attendanceTypes)) break
        filters.push({
          label: t('searchBar.REQUEST_TYPE'),
          icon: 'fa-flag',
          type: 'leaveType',
          key: 74,
          options: attendanceTypes.map(type => {
            return {
              _id: type.id + '0',
              label: t(`searchBar.FILTER_REQUESTS_${type.name}`)
            }
          })
        })
        break
      }

      // - - SHOW LEAVE REASON - - //
      case 'leaveReason': {
        const leaveReasons = attendanceReasons.filter(({ type }) => type === 'leaveReason')
        // Don't add tags if there are no requestFields status
        if (isEmpty(leaveReasons)) break

        filters.push({
          label: t('searchBar.ABSENCE_REASON'),
          type: 'leaveReason',
          icon: 'fa-calendar-times',
          key: 75,
          options: leaveReasons.map(reason => ({
            _id: reason._id,
            label: reason[`name${currentLanguage.toUpperCase()}`]
          }))
        })
        break
      }

      // - - SHOW LATE REASON - - //
      case 'lateReason': {
        const lateReasons = attendanceReasons.filter(({ type }) => type === 'lateReason')

        // Don't add tags if there are no requestFields status
        if (isEmpty(lateReasons)) break

        filters.push({
          label: t('searchBar.LATE_REASON'),
          type: 'lateReason',
          icon: 'fa-alarm-clock',
          key: 76,
          options: lateReasons.map(reason => ({
            _id: reason._id,
            label: reason[`name${currentLanguage.toUpperCase()}`]
          }))
        })
        break
      }

      // - - SHOW EARLY OUT REASON - - //
      case 'earlyOutReason': {
        const earlyOutReasons = attendanceReasons.filter(({ type }) => type === 'earlyOutReason')

        // Don't add tags if there are no requestFields status
        if (isEmpty(earlyOutReasons)) break

        filters.push({
          label: t('searchBar.EARLY_OUT_REASON'),
          type: 'earlyOutReason',
          icon: 'fa-clock',
          key: 77,
          options: earlyOutReasons.map(reason => ({
            _id: reason._id,
            label: reason[`name${currentLanguage.toUpperCase()}`]
          }))
        })
        break
      }

      // - - SHOW CLIENTS SENIORITIES TAGS - - //
      case 'clientSeniorities': {
        const clientSeniorities: OptionsType[] = []
        tags.forEach(({ _id, type, name }) => {
          if (type === 'clients') clientSeniorities.push({ label: name, _id })
        })

        filters.push({
          icon: 'fa fa-suitcase',
          options: clientSeniorities,
          label: t('searchBar.CLIENT_SENIORITIES'),
          type: 'clientSeniorities',
          key: 69
        })
        break
      }
      // - - SHOW POSITION SENIORITIES TAGS - - //
      case 'positionSeniorities': {
        if (isEmpty(positionsOptions)) break
        filters.push({
          label: t('searchBar.POSITION_SENIORITIES'),
          type: 'positionSeniorities',
          icon: 'fa-map-pin',
          key: 70,
          options: positionsOptions.map(position => ({
            label: position.name,
            _id: `seniority-${position._id}`
          }))
        })

        break
      }
      // - - SCHEDULE Templates - - //
      case 'scheduleTemplates': {
        // Make sure that there are schedule types to filter by
        if (isEmpty(scheduleTemplates)) break
        filters.push({
          label: t('searchBar.SCHEDULE_TEMPLATES'),
          icon: 'fa-calendar-plus',
          type: 'scheduleTemplates',
          key: 8,
          options: scheduleTemplates.map(template => ({
            label: template.name,
            _id: template._id
          }))
        })
        break
      }
      // - - HIRING DATA - - //
      case 'hiringData': {
        // Make sure that hiringDatas is defined
        if (isEmpty(hiringData)) break
        if (!hasAccessToThis('hiringDataSee')) break

        filters.push({
          label: t('searchBar.HIRING_DATA'),
          type: 'hiringData',
          icon: 'fa-address-card',
          key: 9,
          options: hiringData.map(hiringData => ({
            label: hiringData.name,
            _id: hiringData._id
          }))
        })
        break
      }
      case 'hiringDataTypes': {
        if (!hasAccessToThis('hiringDataSee')) break

        filters.push({
          label: t('searchBar.HIRING_DATA_TYPES'),
          type: 'type',
          key: 62,
          icon: 'fa-address-card',
          options: [
            {
              label: t('searchBar.CONTRACT_TYPE'),
              _id: 'contractType'
            },
            {
              label: t('searchBar.EMPLOYEE_TYPE'),
              _id: 'employeeType'
            },
            {
              label: t('searchBar.PERMIT_TYPE'),
              _id: 'permitType'
            }
          ]
        })

        break
      }

      // - - PROJECTS - - //
      case 'projects': {
        // Make sure that projects is defined
        if (isEmpty(projectsOptions)) break
        filters.push({
          label: t('searchBar.PROJECT'),
          type: 'projects',
          icon: 'fa-book',
          key: 10,
          options: projectsOptions.map(project => ({
            label: project.name,
            _id: project._id,
            subLabel: `[${project.code}]`,
            alt: project.code
          }))
        })
        break
      }

      // - - NO PROJECT- - //
      case 'noProject': {
        otherFilters.push({
          _id: 'noProject',
          label: t('searchBar.NO_PROJECT'),
          key: 92
        })
        break
      }
      // - - PROJECT CODES - - //
      // case 'projectCodes': {
      //   let projectCodes: OptionsType[] = []
      //   // Make sure that projects is defined
      //   if (isEmpty(projectsOptions)) break
      //   projectsOptions.forEach(project => {
      //     if (project.code) {
      //       projectCodes.push({
      //         _id: project.code,
      //         label: project.code
      //       })
      //     }
      //   })
      //   // Get rid of the duplicate project codes
      //   projectCodes = uniqBy(projectCodes, 'label')
      //   filters.push({
      //     options: projectCodes,
      //     type: 'projectCodes',
      //     icon: 'fa-bookmark',
      //     label: t('searchBar.PROJECT_CODE'),
      //     key: 11
      //   })
      //   break
      // }
      // - - PROJECT TAGS - - //
      case 'projectTags': {
        let projectTags: OptionsType[] = []
        // Go through all projects
        // Make sure that projects is defined
        if (isEmpty(projectsOptions)) break
        projectsOptions.forEach(project => {
          // Init tags if missing
          if (!project.tags) project.tags = []
          // Go through all the project's tag
          project.tags.forEach(tag => {
            projectTags.push({
              _id: tag,
              label: tag
            })
          })
        })
        // Get rid of the duplicate tags
        projectTags = uniqBy(projectTags, 'label')
        filters.push({
          options: projectTags,
          type: 'projectTags',
          icon: 'fa-tag',
          label: t('searchBar.PROJECT_TAG'),
          key: 12
        })
        break
      }
      // - - PROJECT STATUSES - - //
      case 'projectStatuses': {
        // Get rid of the duplicate project codes
        filters.push({
          options: ['CONFIRMED', 'TENTATIVE', 'CANCELLED', 'PLACEHOLDER', 'COMPLETED', 'ARCHIVED'].map(projectStatus => ({
            _id: projectStatus,
            label: t(`manager.projects.STATUSES.${projectStatus}`)
          })),
          type: 'projectStatus',
          icon: 'fa-flag',
          label: t('searchBar.PROJECT_STATUS'),
          key: 63
        })
        break
      }
      case 'lockedProject': {
        if (isEmpty(projectsOptions)) break

        filters.push({
          label: t('searchBar.LOCKED'),
          type: 'lockedProject',
          icon: 'fa-user-lock',
          key: 64,
          options: projectsOptions.map(project => ({
            label: `${project.name} (${t('searchBar.LOCKED')})`,
            _id: `locked-${project._id}`
          }))
        })
        break
      }
      case 'preferredProject': {
        if (isEmpty(projectsOptions)) break

        filters.push({
          label: t('searchBar.PREFERRED'),
          type: 'preferredProject',
          icon: 'fa-user-check',
          key: 65,
          options: projectsOptions.map(project => ({
            label: `${project.name} (${t('searchBar.PREFERRED')})`,
            _id: `preferred-${project._id}`
          }))
        })
        break
      }
      case 'blockedProject': {
        if (isEmpty(projectsOptions)) break

        filters.push({
          label: t('searchBar.BLOCKED'),
          type: 'blockedProject',
          icon: 'fa-user-times',
          key: 66,
          options: projectsOptions.map(project => ({
            label: `${project.name} (${t('searchBar.BLOCKED')})`,
            _id: `blocked-${project._id}`
          }))
        })
        break
      }

      // - - SCHEDULE TYPES - - //
      case 'scheduleTypes': {
        // Make sure that there are schedule types to filter by
        if (isEmpty(scheduleTypes)) break
        filters.push({
          label: t('searchBar.SCHEDULE_TYPES'),
          icon: 'fa-calendar',
          type: 'scheduleTypes',
          key: 14,
          options: scheduleTypes.map(type => ({
            label: type.name,
            _id: type._id
          }))
        })
        break
      }
      // Add filters for checkboxes in shift options
      case 'shiftOptions': {
        const options = [
          'isExtraShift',
          'isOvertime',
          'isOnCall',
          'isIrreplaceable',
          'needsReplacement',
          'noEmployeeAvailable',
          'hasSpecificRequirements',
          'coveredOtherUnit'
        ].map(option => {
          return {
            label: t(`searchBar.${option.toUpperCase()}`),
            _id: option
          }
        })
        filters.push({
          type: 'shiftOptions',
          _id: option,
          label: t(`searchBar.${'shiftOptions'.toUpperCase()}`),
          icon: 'fa-calendar',
          key: 86,
          options
        })

        break
      }
      // - - SHOW VACATION (TIME OFF) TYPE - - //
      case 'vacationType': {
        // Don't add tags if there are no requestFields status
        if (isEmpty(timeOffTypes)) break

        filters.push({
          label: t('searchBar.VACATION_TYPE'),
          type: 'vacationType',
          icon: 'fa-calendar-times',
          key: 15,
          options: timeOffTypes.map(vacationType => ({
            _id: vacationType._id,
            label: vacationType[`name${currentLanguage.toUpperCase()}`]
          }))
        })
        break
      }

      case 'requestOptionTypes': {
        filters.push({
          label: t('searchBar.REQUEST_OPTIONS_TYPES'),
          type: 'type',
          icon: 'fa-calendar-times',
          key: 78,
          options: [
            {
              label: t('searchBar.LEAVE_TYPE'),
              _id: 'leaveReason'
            },
            {
              label: t('searchBar.LATE_TYPE'),
              _id: 'lateReason'
            },
            {
              label: t('searchBar.EARLY_OUT_TYPE'),
              _id: 'earlyOutReason'
            },
            {
              label: t('searchBar.TIME_OFF_TYPE'),
              _id: 'vacationType'
            }
          ]
        })
        break
      }

      case 'requestOptions': {
        filters.push({
          label: t('searchBar.REQUEST_OPTIONS'),
          type: '_ids',
          icon: 'fa-calendar-times',
          key: 79,
          options: [...attendanceReasons, ...timeOffTypes].map(type => ({
            _id: type._id,
            label: type[`name${currentLanguage.toUpperCase()}`]
          }))
        })
        break
      }
      case 'timeOffOnlyCheck': {
        filters.push({
          label: t('searchBar.CHECK_ONLY_TIME_OFF'),
          type: 'timeOffOnlyCheck',
          icon: 'fa-plane',
          key: 80,
          options: timeOffTypes.map(type => ({
            _id: `only-${type._id}`,
            label: t('searchBar.CHECK_ONLY', { name: type[`name${currentLanguage.toUpperCase()}`] })
          }))
        })
        break
      }
      case 'timeOfDontCheck': {
        filters.push({
          label: t('searchBar.DONT_CHECK_TIME_OFF'),
          type: 'timeOfDontCheck',
          icon: 'fa-plane',
          key: 81,
          options: timeOffTypes.map(type => ({
            _id: `not-${type._id}`,
            label: t('searchBar.DONT_CHECK', { name: type[`name${currentLanguage.toUpperCase()}`] })
          }))
        })
        break
      }
      // - - SHOW AVAILBILITY REQUEST STATUS - - //
      case 'availabilityRequestStatus': {
        const statusOption = ['APPROVAL_REQUIRED', 'APPROVED', 'CHANGES_REQUESTED']

        filters.push({
          label: t('searchBar.REQUEST_STATUS'),
          type: 'availabilityRequestStatus',
          icon: 'fa-flag',
          key: 82,
          options: statusOption.map(status => ({
            _id: status,
            label: t(status)
          }))
        })
        break
      }

      // - - SHOW ABSENCES OVER THE LIMIT - - //
      case 'absenceThresholdReached': {
        otherFilters.push({
          _id: 'absenceThresholdReached',
          label: t('searchBar.SHOW_ABSENCES_OVER_THRESHOLD'),
          key: 83
        })
        break
      }

      // - - Filter by error in integration - - //
      case 'integrationError': {
        otherFilters.push({
          _id: 'integrationError',
          label: t('searchBar.SHOW_INTEGRATION_ERRORS'),
          key: 84
        })
        break
      }

      case 'flags': {
        // Search flags
        filters.push({
          label: t('searchBar.PUNCH_FLAG'),
          type: 'flags',
          icon: 'fa-flag',
          key: 16,
          options: [
            ...['isInEarly', 'isInLate', 'isOutEarly', 'isOutLate'].map(flagType => ({
              _id: `${flagType}:all`,
              label: t(`searchBar.punch.${flagType.toUpperCase()}`)
            })),
            {
              _id: 'notPunchedIn',
              label: t('searchBar.punch.FLAG_NOT_PUNCHED_IN')
            },
            {
              _id: 'notPunchedOut',
              label: t('searchBar.punch.FLAG_NOT_PUNCHED_OUT')
            },
            {
              _id: 'duplicateCard',
              label: t('searchBar.punch.FLAG_DUPLICATE_SHIFT')
            },
            { _id: 'noShift', label: t('searchBar.punch.FLAG_NO_SHIFT') },
            {
              _id: 'noClockedTime',
              label: t('searchBar.punch.FLAG_NO_CLOCKED_TIME')
            },
            {
              _id: 'locationConflicts',
              label: t('searchBar.punch.FLAG_LOCATION_CONFLICTS')
            },
            {
              _id: 'isOnTimeoff',
              label: t('searchBar.punch.FLAG_OVERLAPPING_TIMEOFF')
            },
            {
              _id: 'isAbsent',
              label: t('searchBar.punch.FLAG_IS_ABSENT')
            }
          ]
        })
        break
      }

      case 'punchCardStatus': {
        filters.push({
          label: t('searchBar.PUNCH_STATUS'),
          type: 'punchCardStatus',
          icon: 'fa-flag',
          key: 17,
          options: ['APPROVED', 'APPROVAL_REQUIRED'].map(status => ({
            _id: status,
            label: t(`searchBar.${status}`)
          }))
        })
        break
      }
      case 'hasComments': {
        otherFilters.push({
          _id: 'hasComments',
          label: t('searchBar.HAS_COMMENTS'),
          key: 87
        })
        break
      }

      case 'hasTransfers': {
        otherFilters.push({
          _id: 'hasTransfers',
          label: t('searchBar.HAS_TRANSFERS'),
          key: 88
        })
        break
      }

      case 'hasIssues': {
        // Has issues
        otherFilters.push({
          _id: 'hasIssues',
          label: t('searchBar.HAS_ISSUES'),
          key: 18
        })
        break
      }

      case 'actionRequired': {
        // Requires action
        otherFilters.push({
          _id: 'actionRequired',
          label: t('searchBar.REQUIRES_ACTION'),
          key: 19
        })
        break
      }

      case 'selfAssignable': {
        otherFilters.push({
          _id: 'selfAssign',
          label: t('searchBar.SELFASSIGN'),
          key: 20
        })
        break
      }

      case 'cannotBeSelfAssigned': {
        otherFilters.push({
          _id: 'cannotBeSelfAssigned',
          label: t('searchBar.CANNOTBESELFASSIGNED'),
          key: 21
        })
        break
      }
      case 'nonAssignable': {
        otherFilters.push({
          _id: 'nonAssignable',
          label: t('searchBar.NON_ASSIGNABLE'),
          key: 22
        })
        break
      }

      case 'contactListStatuses': {
        filters.push({
          label: t('searchBar.CONTACT_LIST_STATUS'),
          type: 'status',
          icon: 'fa-flag',
          key: 23,
          options: [
            ...['ARCHIVED', 'CREATED', 'PAUSED', 'RUNNING'].map(listStatus => ({
              _id: listStatus,
              label: t(`searchBar.contactLists.${listStatus}`)
            }))
          ]
        })
        break
      }
      case 'contactUserStatuses': {
        filters.push({
          label: t('searchBar.CONTACT_USER_FLAG'),
          type: 'contactUserStatuses',
          icon: 'fa-flag',
          key: 24,
          options: [
            ...[
              'shouldCall',
              'shouldNotCall',
              'calledManually',
              'notCalledManually',
              'notContactedYet',
              'contactCompleted',
              'isInDelay',
              'hasBeenBumped',
              'accepted',
              'refused',
              'otherCheckNotes'
            ].map(callStatus => ({
              _id: callStatus,
              label: t(`searchBar.contactUsers.${callStatus.toUpperCase()}`)
            }))
          ]
        })

        // Is eligible
        otherFilters.push({
          _id: 'isEligible',
          label: t('searchBar.IS_ELIGIBLE'),
          key: 25
        })

        otherFilters.push({
          _id: 'currentlyCalling',
          label: t('searchBar.CURRENTLY_CALLING'),
          key: 26
        })
        break
      }
      case 'quickAssignOptions': {
        otherFilters.push({
          label: t('searchBar.IS_NOT_WORKING'),
          _id: 'eventOverlapping',
          key: 27
        })

        otherFilters.push({
          label: t('searchBar.IS_AVAILBLE'),
          _id: 'unavailableAllDay',
          key: 28
        })

        otherFilters.push({
          label: t('searchBar.IS_NOT_ON_TIME_OFF'),
          _id: 'vacationOverlapping',
          key: 29
        })

        otherFilters.push({
          label: t('searchBar.NO_VACATION_DURING_WEEK'),
          _id: 'vacationDuringWeek',
          key: 30
        })

        otherFilters.push({
          label: t('searchBar.CHECK_PARTIAL_TIME_OFF_ONLY'),
          _id: 'partialTimeOffOnly',
          key: 31
        })

        otherFilters.push({
          label: t('searchBar.IGNORE_PARTIAL_TIME_OFF'),
          _id: 'partialTimeOffIgnore',
          key: 32
        })
        break
      }
      case 'questionnaireResponses': {
        otherFilters.push(
          {
            label: t('manager.reports.QUESTIONNAIRE_RESPONSES.NOT_ALLOWED_TO_PUNCH'),
            _id: 'notAllowedToPunch',
            key: 33
          },
          {
            label: t('manager.reports.QUESTIONNAIRE_RESPONSES.ERROR_TRIGGERED'),
            _id: 'errorTriggered',
            key: 34
          }
        )
        break
      }
      case 'userList': {
        otherFilters.push(
          {
            _id: 'requestedChanges',
            label: t('searchBar.ASKED_FOR_CHANGES'),
            key: 35
          },
          {
            _id: 'notOnboarded',
            label: t('searchBar.NOT_ONBOARDED'),
            key: 36
          },
          {
            _id: 'neverConnected',
            label: t('searchBar.NEVER_CONNECTED'),
            key: 37
          },
          {
            label: t('searchBar.NO_EMAIL_SET'),
            _id: 'noEmail',
            key: 38
          },
          {
            label: t('searchBar.NO_PHONE_NUMBER_SET'),
            _id: 'noPhoneNumbers',
            key: 39
          },
          {
            label: t('searchBar.IS_ARCHIVED'),
            _id: 'archived',
            key: 39
          }
        )
        break
      }
      case 'schedule': {
        otherFilters.push(
          {
            label: t('searchBar.FILTER_REQUESTS_ABSENCE'),
            _id: 'absence',
            key: 40
          },
          {
            label: t('searchBar.FILTER_REQUESTS_DELAY'),
            _id: 'late',
            key: 41
          },
          {
            label: t('searchBar.FILTER_REQUESTS_EARLY_OUT'),
            _id: 'earlyOut',
            key: 42
          },
          {
            label: t('searchBar.FILTER_REQUESTS_VACATION'),
            _id: 'vacation',
            key: 43
          },
          {
            label: t('searchBar.FILTER_REQUESTS_ALL'),
            _id: 'all',
            key: 44
          },
          {
            label: t('searchBar.SHOW_ALL_POSITIONS'),
            _id: 'showAllPositions',
            key: 45
          },
          {
            _id: 'showAllScheduledProjects',
            label: t('searchBar.SHOW_ALL_SCHEDULED_PROJECTS'),
            key: 46
          },
          {
            _id: 'noPunchCard',
            label: t('searchBar.SHOW_UNPUNCHED_SHIFTS'),
            key: 47
          },
          {
            _id: 'unpublished',
            label: t('searchBar.UNPUBLISHED_SHIFTS'),
            key: 48
          },
          {
            _id: 'showUnconfirmedByUser',
            label: t('searchBar.UNCONFIRMED_BY_USER'),
            key: 49
          }
        )
        break
      }

      // - - REPLACED SHIFTS - - //
      case 'replacedShifts': {
        otherFilters.push({
          _id: 'showReplacedShifts',
          label: t('searchBar.REPLACED'),
          key: 51
        })
        break
      }

      // - - UNASSIGNED SHIFTS - - //
      case 'unassignedShifts': {
        otherFilters.push({
          _id: 'unassigned',
          label: t('searchBar.UNASSIGNED'),
          key: 52
        })
        break
      }

      // - - CANCELLED SHIFTS - - //
      case 'cancelledShifts': {
        otherFilters.push({
          label: t('searchBar.CANCELLED'),
          _id: 'showCancelledOnly',
          key: 53
        })
        break
      }

      // - - SHOW FINALIZED SHIFTS - - //
      case 'showFinalized': {
        otherFilters.push({
          label: t('searchBar.SHOW_FINALIZED'),
          _id: 'finalized',
          key: 54
        })
        break
      }

      // - - HIDE FINALIZED SHIFTS - - //
      case 'hideFinalized': {
        otherFilters.push({
          label: t('searchBar.HIDE_FINALIZED'),
          _id: '-finalized',
          key: 55
        })
        break
      }

      // - - SHOW LOCATION TAGS - - //
      case 'locations': {
        const locations: OptionsType[] = []
        tags.forEach(({ _id, type, name, address, icon }) => {
          if (type === 'locations')
            locations.push({
              label: name,
              subLabel: address ? <Address address={address} short /> : undefined,
              _id,
              icon
            })
        })
        filters.push({
          icon: 'fa-building',
          options: locations,
          label: t('searchBar.LOCATION'),
          type: 'locations',
          key: 56
        })
        break
      }

      // - - SHOW CUSTOM TAGS - - //
      case 'customTags': {
        const customTags: OptionsType[] = []
        tags.forEach(({ _id, type, name, icon, color }) => {
          if (type === 'custom')
            customTags.push({
              label: name,
              color,
              _id,
              icon
            })
        })
        filters.push({
          icon: 'fa-tag',
          options: customTags,
          label: t('searchBar.CUSTOMTAG'),
          type: 'customTags',
          key: 91
        })
        break
      }
      // - - SHOW RESOURCES TAGS - - //
      case 'resources': {
        const resources: OptionsType[] = []
        tags.forEach(({ _id, type, name, icon }) => {
          if (type === 'resources') resources.push({ label: name, _id, icon })
        })

        filters.push({
          icon: 'fa-barcode-read',
          options: resources,
          label: t('searchBar.RESOURCES'),
          type: 'resources',
          key: 57
        })
        break
      }

      case 'auditLog': {
        otherFilters.push(
          { label: 'Merinio - Admin', _id: '000000000000000000000000', key: '000000000000000000000000' },
          { label: 'Merinio', _id: 'Merinio', key: 'Merinio' },
          { label: 'API Integration', _id: 'aaaaaaaaaaaaaaaaaaaaaaaa', key: 'aaaaaaaaaaaaaaaaaaaaaaaa' }
        )

        // All of the different modules in the audit log
        const modules = [
          'attributeLevel',
          'attributes',
          'authentication',
          'availability',
          'bulletin',
          'contactListConfigss',
          'contactLists',
          'contactTemplates',
          'departmentModification',
          'departments',
          'emails',
          'finalize',
          'generateSchedule',
          'groupsPermission',
          'hiringData',
          'leave',
          'pay',
          'permissions',
          'projects',
          'punch',
          'punchRules',
          'requestsFields',
          'resolve',
          'schedule',
          'scheduleConfirmation',
          'scheduleTemplate',
          'scheduleTypes',
          'settings',
          'shift-cancellation-email',
          'shift-confirmation-email',
          'shift-confirmation-phone',
          'structure',
          'structureAttributes',
          'tags',
          'teams',
          'twilio',
          'user',
          'userAttributes',
          'userDepartments',
          'userEmails',
          'userFilters',
          'userHiringData',
          'userPermissions',
          'userPhoneNumbers',
          'userPicture',
          'userPronouns',
          'userPositions',
          'userProcedures',
          'regionalTags',
          'clients',
          'userSorts',
          'userTeams',
          'users',
          'vacationModule'
        ]

        otherFilters.push(
          ...modules.map(mod => ({
            label: t(`manager.audit.modules.${mod}`),
            _id: mod,
            key: 85
          }))
        )
        break
      }

      case 'projectRestrictions':
      case 'punchCards': {
        break
      }
      default: {
        break
      }
    }
  })

  if (!isEmpty(otherFilters)) {
    filters.unshift({
      label: t('searchBar.CONTEXTUAL_FILTERS'),
      type: 'others',
      icon: 'fa-filter',
      key: 100,
      options: otherFilters
    })
  }

  return filters
}

export const scheduleSearchOptions = [
  'users',
  'positions',
  'departments',
  'projects',
  'scheduleTypes',
  'teams',
  'reportsTo',
  'shiftOptions',
  'unassignedShifts',
  'replacedShifts',
  'cancelledShifts',
  'showFinalized',
  'hideFinalized',
  'clients',
  'locations',
  'regionalTags',
  'resources',
  'customTags',
  'eligibleForPosition',
  'vacationType',
  'schedule',
  'hiringData',
  'scheduleTemplates',
  'projectCodes',
  'noProject'
]
